import type { AuditEvent } from '@/types/api'
import { relativeTime, exactTimestamp } from '@/utils/time'
import { EmptyState } from './States'
import { IconDocuments } from './Icons'

function severityFromEvent(event: AuditEvent): 'ok' | 'crit' | 'warn' | 'info' {
  const action = (event.action || '').toLowerCase()
  const status = (event.status || '').toLowerCase()

  if (action.includes('expire') || status === 'expired') return 'warn'
  if (action.includes('void') || action.includes('fail') || action.includes('decline') || status === 'failure') return 'crit'
  if (action.includes('sign') || action.includes('complete') || status === 'success') return 'ok'
  return 'info'
}

const dotStyles: Record<string, string> = {
  ok: 'bg-emerald-500 ring-4 ring-emerald-100 shadow-sm',
  warn: 'bg-amber-500 ring-4 ring-amber-100 shadow-sm animate-pulse',
  crit: 'bg-rose-500 ring-4 ring-rose-100 shadow-sm',
  info: 'bg-sky-500 ring-4 ring-sky-100 shadow-sm',
}

export function ActivityTimeline({
  events = [],
  onSelect,
}: {
  events?: AuditEvent[] | null
  onSelect?: (event: AuditEvent) => void
}) {
  const safeEvents = events ?? []
  if (safeEvents.length === 0) {
    return <EmptyState title="No activity recorded" description="Events will appear here in real-time as they happen." />
  }

  return (
    <ol className="relative ml-2 border-l border-slate-200/90 pl-6 space-y-3">
      {safeEvents.map((event) => {
        const sev = severityFromEvent(event)
        const docTitle = event.documentTitle || event.document_title
        const docId = event.documentId || event.document_id
        const isExpired = event.action?.toLowerCase().includes('expire')

        return (
          <li key={event.id || Math.random()} className="group relative">
            {/* Timeline Dot Indicator */}
            <span className={`absolute -ml-[29px] mt-2 h-2.5 w-2.5 rounded-full ${dotStyles[sev]} transition-transform group-hover:scale-125`} />

            <button
              onClick={() => onSelect?.(event)}
              className="w-full rounded-xl border border-slate-200/80 bg-white p-3 text-left transition-all duration-150 hover:border-sky-300 hover:bg-sky-50/30 hover:shadow-xs focus:outline-none"
              disabled={!onSelect}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5 flex-wrap">
                    <span>{event.actorName || event.actor || 'System'}</span>
                    <span className="font-mono text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200/60">
                      {event.action}
                    </span>
                    {isExpired && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.2 text-[9px] font-bold text-amber-800 border border-amber-300">
                        EXPIRED
                      </span>
                    )}
                  </div>

                  {/* Document info snippet */}
                  {(docTitle || docId) && (
                    <div className="mt-1 text-xs font-semibold text-sky-700 flex items-center gap-1.5">
                      <IconDocuments className="h-3.5 w-3.5 text-sky-600" />
                      <span className="truncate max-w-[280px]" title={docTitle || docId}>
                        {docTitle || `Doc #${docId?.slice(-6)}`}
                      </span>
                    </div>
                  )}
                </div>

                <span className="shrink-0 font-mono text-[10.5px] font-medium text-slate-400" title={exactTimestamp(event.timestamp)}>
                  {relativeTime(event.timestamp)}
                </span>
              </div>

              {event.ipAddress && (
                <div className="mt-1.5 font-mono text-[10px] text-slate-400">
                  IP: {event.ipAddress} {event.orgName ? `· ${event.orgName}` : ''}
                </div>
              )}
            </button>
          </li>
        )
      })}
    </ol>
  )
}
